import React, { useState, useEffect } from 'react';
import AdminDashboardLayout from 'pages/admin-dashboard-overview/components/AdminDashboardLayout';
import { supabase } from 'lib/supabase';
import { format } from 'date-fns';
import { FileText } from 'lucide-react';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { value: '', label: 'Tutti gli stati' },
  { value: 'sent', label: 'Inviato' },
  { value: 'shortlisted', label: 'In shortlist' },
  { value: 'accepted', label: 'Accettato' },
];

const STATUS_BADGE = {
  sent: 'bg-blue-100 text-blue-800',
  shortlisted: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
};

const AdminPreventiviPage = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const fetchQuotes = async (page, status) => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase?.from('quotes')?.select(`
          id,
          preview_price,
          status,
          created_at,
          pdf_url,
          professional_id,
          service_request_id,
          users!quotes_professional_id_fkey (email),
          service_requests!quotes_service_request_id_fkey (title)
        `, { count: 'exact' })?.order('created_at', { ascending: false })?.range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

      if (status) {
        query = query?.eq('status', status);
      }

      const { data, count, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setQuotes(data || []);
      setTotalCount(count || 0);
    } catch (err) {
      setError(err?.message || 'Errore nel caricamento dei preventivi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes(currentPage, statusFilter);
  }, [currentPage, statusFilter]);

  const handleStatusChange = (e) => {
    setStatusFilter(e?.target?.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    try { return format(new Date(ts), 'dd/MM/yyyy HH:mm'); } catch { return ts; }
  };

  const formatPrice = (price) => {
    if (price == null) return '-';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' })?.format(price);
  };

  return (
    <AdminDashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Preventivi</h1>
          <p className="text-sm text-gray-500 mt-1">Tutti i preventivi inviati dai professionisti</p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <select
            value={statusFilter}
            onChange={handleStatusChange}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {STATUS_OPTIONS?.map((opt) => (
              <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
            ))}
          </select>
          <span className="text-sm text-gray-500">{totalCount} risultati</span>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email professionista</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Richiesta</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prezzo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data invio</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PDF</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-3">Caricamento...</span>
                      </div>
                    </td>
                  </tr>
                ) : quotes?.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      Nessun preventivo trovato
                    </td>
                  </tr>
                ) : (
                  quotes?.map((quote) => (
                    <tr key={quote?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {quote?.users?.email || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {quote?.service_requests?.title || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatPrice(quote?.preview_price)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE?.[quote?.status] || 'bg-gray-100 text-gray-700'}`}>
                          {quote?.status || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatDate(quote?.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        {quote?.pdf_url ? (
                          <button
                            onClick={() => window.open(quote?.pdf_url, '_blank')}
                            title="Apri PDF"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                          >
                            <FileText size={18} color="#104d98" />
                          </button>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <p className="text-sm text-gray-700">
                  Risultati <span className="font-medium">{(currentPage - 1) * PAGE_SIZE + 1}</span>–
                  <span className="font-medium">{Math.min(currentPage * PAGE_SIZE, totalCount)}</span> di{' '}
                  <span className="font-medium">{totalCount}</span>
                </p>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Precedente
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Successiva
                  </button>
                </nav>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminDashboardLayout>
  );
};

export default AdminPreventiviPage;
